/**
 * PmsOwnerStays — Owner stays (admin).
 *
 * Resumen: solicitudes · próximos · completados · rechazados.
 * Cada request muestra validaciones (availability, notice ≥30 días, quota
 * ≤60 nights/year, blackout OWNER_STAY_BLACKOUT). Approve/Reject persisten
 * en `pmsRuntimeStore` — approve crea un OWNER_STAY block ligado; reject
 * guarda motivo, sin block. Cualquier consumidor (Investor, Calendar,
 * Allocation) reacciona sin refresh.
 */
import React, { useMemo, useState } from "react";
import { Section } from "@/pages/Shell";
import PmsShell from "@/pages/admin/pms/PmsShell";
import {
  Bed, CheckCircle, Prohibit, User as UserIcon, Warning, Info, X,
} from "@phosphor-icons/react";
import { DEMO_TODAY_ISO } from "@/data/pmsDemoData";
import { getVilla, ownerStayValidation, ownerStayUsage } from "@/lib/pmsSelectors";
import {
  usePmsOwnerStays, usePmsBlocks,
  approveOwnerStayRequest, rejectOwnerStayRequest,
} from "@/lib/pmsRuntimeStore";

const TABS = [
  { key: "requested",  label: "Solicitudes"  },
  { key: "upcoming",   label: "Próximos"     },
  { key: "completed",  label: "Completados"  },
  { key: "rejected",   label: "Rechazados"   },
];

const REJECT_REASONS = [
  { value: "dates_unavailable", label: "Dates unavailable" },
  { value: "policy_blackout",   label: "Policy / blackout" },
  { value: "annual_limit",      label: "Annual limit" },
  { value: "other",             label: "Other" },
];

function bucketOf(r, today) {
  if (r.status === "requested") return "requested";
  if (r.status === "rejected")  return "rejected";
  if (r.status === "cancelled") return "rejected";
  if (r.status === "completed") return "completed";
  // approved
  if (r.end_date < today) return "completed";
  return "upcoming";
}

export default function PmsOwnerStays() {
  const today = DEMO_TODAY_ISO;
  // Subscripción reactiva: cualquier alta desde el Investor demo aparece de
  // inmediato en "Solicitudes"; approve/reject se propagan a Calendar
  // (blocks) y a la lista.
  const requests = usePmsOwnerStays();
  usePmsBlocks();
  const [tab, setTab] = useState("requested");
  const [rejecting, setRejecting] = useState(null); // request_id | null

  const counts = useMemo(() => {
    const c = { requested: 0, upcoming: 0, completed: 0, rejected: 0 };
    for (const r of requests) c[bucketOf(r, today)]++;
    return c;
  }, [requests, today]);

  const filtered = useMemo(
    () => requests
      .filter((r) => bucketOf(r, today) === tab)
      .sort((a, b) => (a.start_date < b.start_date ? 1 : -1)),
    [requests, tab, today],
  );

  const approve = (id) => approveOwnerStayRequest(id);

  return (
    <PmsShell>
      <Section
        title="Owner stays"
        subtitle="Solicitudes de propietarios · 60 noches/año · ≥30 días de antelación · fuera de blackout."
      >
        {/* Tabs */}
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="pms-owner-stays-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`pms-owner-stays-tab-${t.key}`}
              className={`text-xs px-3 py-1.5 rounded-full ${
                tab === t.key
                  ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
                  : "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700"
              }`}
            >
              {t.label}
              <span className="ml-1.5 tabular-nums opacity-70">{counts[t.key] || 0}</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="dv-card text-sm text-stone-500 dark:text-stone-400 text-center py-10">
            No hay solicitudes en esta pestaña.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="pms-owner-stays-list">
            {filtered.map((r) => (
              <RequestCard
                key={r.request_id}
                req={r}
                onApprove={() => approve(r.request_id)}
                onReject={() => setRejecting(r.request_id)}
              />
            ))}
          </div>
        )}

        <div className="dv-card mt-6 border-l-4 border-l-amber-400" data-testid="pms-owner-stays-rules">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-amber-600 dark:text-amber-400 mt-0.5" weight="fill" />
            <div className="text-sm text-stone-700 dark:text-stone-300 space-y-1">
              <div className="font-semibold text-stone-900 dark:text-stone-100">Reglas visuales</div>
              <ul className="list-disc list-inside">
                <li>Máximo 60 noches por villa/año — sólo approved+completed consumen cupo.</li>
                <li>Solicitud con antelación ≥ 30 días respecto al check-in.</li>
                <li>Villa debe estar disponible; no desplaza reservas confirmadas.</li>
                <li>Blackout OWNER_STAY (MotoGP 15–22 Oct) queda bloqueado.</li>
                <li>Approve crea un OWNER_STAY block en el calendar (no compensable en fairness).</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {rejecting && (
        <RejectModal
          requestId={rejecting}
          onClose={() => setRejecting(null)}
          onConfirm={(reason) => {
            rejectOwnerStayRequest(rejecting, reason);
            setRejecting(null);
          }}
        />
      )}
    </PmsShell>
  );
}

function RequestCard({ req, onApprove, onReject }) {
  const villa = getVilla(req.villa_id);
  const usage = ownerStayUsage(req.villa_id, req.start_date.slice(0, 4));
  const val = ownerStayValidation(req);
  const canDecide = req.status === "requested";
  const isNew = req.origin === "created_in_session";

  return (
    <div className="dv-card" data-testid={`pms-owner-stay-${req.request_id}`} data-status={req.status}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">
            {req.request_id}
            {isNew && (
              <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                Nueva
              </span>
            )}
          </div>
          <div className="mt-0.5 font-medium text-stone-900 dark:text-stone-100">
            {villa?.villa_id} · {villa?.name}
          </div>
          <div className="text-xs text-stone-500 dark:text-stone-400 inline-flex items-center gap-1 mt-0.5">
            <UserIcon size={11} /> {req.investor_name || req.investor_user_id}
          </div>
        </div>
        <StatusBadge status={req.status} />
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
        <Meta label="Check-in"  value={req.start_date} />
        <Meta label="Check-out" value={req.end_date} />
        <Meta label="Noches"    value={String(req.nights)} />
        <Meta label="Solicitado" value={req.requested_at?.slice(0, 10) || "—"} />
        <Meta label="Antelación" value={val.notice_days != null ? `${val.notice_days} días` : "—"} tone={val.notice_ok ? "ok" : "bad"} />
        <Meta label="Uso anual"  value={`${usage.used} / 60`} tone={usage.remaining > 0 ? "ok" : "bad"} />
      </div>

      <div className="mt-3 space-y-1">
        <Check label="Villa disponible"      ok={val.availability_ok} detail={val.conflicts.slice(0, 2).join(" · ")} />
        <Check label="Antelación ≥ 30 días"  ok={val.notice_ok}      detail={val.notice_days != null && !val.notice_ok ? `Solo ${val.notice_days}d` : ""} />
        <Check label="Dentro de cupo (60/año)" ok={val.quota_ok}      detail={`Tras aprobar: ${val.quota_projected}/60`} />
        <Check label="Fuera de blackout"      ok={val.blackout_ok}   detail={val.blackout ? val.blackout.label : ""} />
      </div>

      {req.rejection_reason && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800 p-2 text-[11px] text-red-800 dark:text-red-200">
          Motivo del rechazo: {req.rejection_reason}
        </div>
      )}

      {req.note && !req.rejection_reason && (
        <div className="mt-3 text-[11px] text-stone-500 dark:text-stone-400 italic">
          {req.note}
        </div>
      )}

      {canDecide && (
        <div className="mt-4 flex flex-wrap gap-2">
          <ActionBtn onClick={onApprove} icon={CheckCircle} primary disabled={!val.all_ok}>
            Aprobar
          </ActionBtn>
          <ActionBtn onClick={onReject} icon={Prohibit} danger>
            Rechazar
          </ActionBtn>
          {!val.all_ok && (
            <span className="text-[11px] text-stone-500 dark:text-stone-400 inline-flex items-center gap-1">
              <Warning size={11} /> Falla al menos una validación
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function RejectModal({ requestId, onClose, onConfirm }) {
  const [reasonKey, setReasonKey] = useState("dates_unavailable");
  const [detail, setDetail] = useState("");
  const label = REJECT_REASONS.find((r) => r.value === reasonKey)?.label || "Rechazado";
  const requiresDetail = reasonKey === "other";
  const canConfirm = !requiresDetail || detail.trim().length > 0;

  const handleConfirm = () => {
    if (!canConfirm) return;
    const composed = detail.trim() ? `${label} — ${detail.trim()}` : label;
    onConfirm(composed);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-stone-950 w-full sm:max-w-[440px] sm:rounded-lg rounded-t-2xl shadow-xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">Rechazar solicitud</div>
            <div className="text-lg font-semibold text-stone-900 dark:text-stone-100">{requestId}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center" aria-label="Cerrar">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">Motivo *</div>
            <select
              value={reasonKey}
              onChange={(e) => setReasonKey(e.target.value)}
              className="w-full text-sm px-2 py-2 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200"
              data-testid="pms-owner-reject-reason"
            >
              {REJECT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">
              Detalle {requiresDetail ? "*" : "(opcional)"}
            </div>
            <input
              type="text"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder={requiresDetail ? "Describe brevemente" : "Extra opcional"}
              className="w-full text-sm px-2 py-2 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200"
              data-testid="pms-owner-reject-detail"
            />
          </label>
          <div className="pt-2 flex justify-end gap-2">
            <button onClick={onClose} className="text-sm px-3 py-2 rounded border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200">
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className={`text-sm px-3 py-2 rounded inline-flex items-center gap-1.5 ${
                canConfirm
                  ? "bg-red-600 dark:bg-red-500 text-white hover:opacity-90"
                  : "bg-stone-200 dark:bg-stone-800 text-stone-400 dark:text-stone-500 cursor-not-allowed"
              }`}
              data-testid="pms-owner-reject-confirm"
            >
              <Prohibit size={14} weight="bold" /> Confirmar rechazo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    requested: { label: "Pendiente",   cls: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200" },
    approved:  { label: "Aprobada",    cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200" },
    completed: { label: "Completada",  cls: "bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-200" },
    rejected:  { label: "Rechazada",   cls: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200" },
    cancelled: { label: "Cancelada",   cls: "bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400" },
  };
  const it = map[status] || map.requested;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${it.cls}`}>
      <Bed size={10} /> {it.label}
    </span>
  );
}

function Meta({ label, value, tone }) {
  const cls = tone === "ok"
    ? "text-emerald-700 dark:text-emerald-300"
    : tone === "bad"
    ? "text-red-700 dark:text-red-300"
    : "text-stone-900 dark:text-stone-100";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-stone-400">{label}</div>
      <div className={`tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function Check({ label, ok, detail }) {
  return (
    <div className={`text-xs flex items-center gap-2 ${ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}`}>
      {ok ? <CheckCircle size={12} weight="fill" /> : <Prohibit size={12} weight="fill" />}
      <span>{label}</span>
      {detail && <span className="text-[11px] text-stone-500 dark:text-stone-400">· {detail}</span>}
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, primary, danger, disabled, children }) {
  const cls = disabled
    ? "border border-stone-300 dark:border-stone-700 text-stone-400 dark:text-stone-500 cursor-not-allowed opacity-70"
    : primary
    ? "bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90"
    : danger
    ? "border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:border-red-500"
    : "border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-500";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`text-xs px-3 py-1.5 rounded inline-flex items-center gap-1.5 min-h-[36px] ${cls}`}
    >
      {Icon && <Icon size={12} weight="bold" />}
      {children}
    </button>
  );
}
